import { useCallback, useEffect } from 'react';
import { useWalletStore } from '@/store/walletStore';
import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from '@/config/currencies';

const RATES_CACHE_KEY = 'exchange_rates_cache';
const CACHE_DURATION = 60 * 60 * 1000;

export function useCurrency() {
  const {
    selectedCurrency,
    setSelectedCurrency,
    exchangeRates,
    setExchangeRates
  } = useWalletStore();

  /**
   * Obtiene las tasas de cambio actualizadas
   */
  const fetchRates = useCallback(async () => {
    try {
      // 1. Verificar cache en localStorage
      const cachedData = localStorage.getItem(RATES_CACHE_KEY);
      if (cachedData) {
        const { rates, timestamp } = JSON.parse(cachedData);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setExchangeRates(rates);
          return;
        }
      }

      // 2. Si no hay cache o expiró, consultar API
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();

      if (data && data.rates) {
        const rates = data.rates;
        setExchangeRates(rates);

        // Guardar en cache
        localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
          rates,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error('Error obteniendo tasas de cambio:', error);
    }
  }, [setExchangeRates]);

  // Cargar tasas al montar si no hay suficientes
  useEffect(() => {
    if (Object.keys(exchangeRates).length <= 1) {
      fetchRates();
    }
  }, [exchangeRates, fetchRates]);

  /**
   * Convierte un monto en USD a la moneda seleccionada
   */
  const convertFromUSD = useCallback((usdAmount: number) => {
    const rate = exchangeRates[selectedCurrency.toUpperCase()] || exchangeRates[selectedCurrency.toLowerCase()] || 1;
    return usdAmount * rate;
  }, [selectedCurrency, exchangeRates]);

  /**
   * Formatea un valor numérico según la moneda seleccionada
   */
  const formatValue = useCallback((usdAmount: number, minimumFractionDigits = 5) => {
    const convertedAmount = convertFromUSD(usdAmount);
    const config = SUPPORTED_CURRENCIES[selectedCurrency] || SUPPORTED_CURRENCIES[DEFAULT_CURRENCY];

    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.code.toUpperCase(),
      minimumFractionDigits,
      maximumFractionDigits: minimumFractionDigits,
    }).format(convertedAmount);
  }, [selectedCurrency, convertFromUSD]);

  /**
   * Cambia la moneda seleccionada
   */
  const changeCurrency = useCallback((currencyCode: string) => {
    if (SUPPORTED_CURRENCIES[currencyCode]) {
      setSelectedCurrency(currencyCode);
    }
  }, [setSelectedCurrency]);

  return {
    selectedCurrency,
    formatValue,
    convertFromUSD,
    changeCurrency,
    currencies: Object.values(SUPPORTED_CURRENCIES),
    currentCurrencyConfig: SUPPORTED_CURRENCIES[selectedCurrency] || SUPPORTED_CURRENCIES[DEFAULT_CURRENCY]
  };
}

export default useCurrency;
